import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogContent,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Icon,
  DialogTitle,
  Collapse,
  Typography,
} from "@material-ui/core";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";

import EmployeeStoreLogo from "../header/components/EmployeeStoreLogo";
import useInfo from "../../store/hooks/useInfo";
import {
  formatPriceWithCountry,
  MAP_COUNTRY_CURRENCY,
} from "../../util/util-ems";
import { AuthContext } from "../../../../core/providers/AuthContext";
import { isNil } from "lodash";

const WIDTH_COLUMNS = 150;

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: "0px 32px 32px",
    gap: "16px",
    display: "flex",
    flexDirection: "column",
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: "70vh",
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    width: WIDTH_COLUMNS,
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  parentRow: {
    backgroundColor: theme.palette.grey[200],
    fontWeight: "bold",
    cursor: "pointer",
  },
  childRow: {
    paddingLeft: theme.spacing(4),
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  },
  indentCell: {
    paddingLeft: theme.spacing(4),
    width: WIDTH_COLUMNS,
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "move",
  },
  dialogPaper: {
    backgroundColor: "rgba(255, 255, 255)",
    backdropFilter: "blur(5px)",
  },
  expanderCell: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: "14px",
    width: WIDTH_COLUMNS,
  },
  commonCell: {
    width: WIDTH_COLUMNS,
  },
  titleText: {
    fontSize: "0.9rem",
    fontWeight: "bold",
  },
  closeBtn: {
    position: "absolute",
    top: 10,
    right: 10,
  },
}));

const ALCANCE_GENERAL = "GEN";
const ALCANCE_DIVISION = "DIV";
const ALCANCE_FIRMA = "FIR";

const MAP_ALCANCE_CODES = {
  [ALCANCE_GENERAL]: "EMPLOYEE_STORE.ALCANCE_GENERAL",
  [ALCANCE_DIVISION]: "EMPLOYEE_STORE.ALCANCE_DIVISION",
  [ALCANCE_FIRMA]: "EMPLOYEE_STORE.ALCANCE_FIRM",
};

function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-ems-current-amounts-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds="parent"
    >
      <Paper {...props} />
    </Draggable>
  );
}

export default function EmployeeStoreLayoutEmsCurrentAmountsDialog() {
  const { t } = useTranslation();
  const classes = useStyles();
  const info = useInfo();
  const { auth } = useContext(AuthContext);
  const [expandedGroups, setExpandedGroups] = useState({
    [ALCANCE_DIVISION]: true,
    [ALCANCE_FIRMA]: true,
  });

  if (info?.currentAmountsInfo?.showCurrentAmountsInfoDialog !== true) {
    return null;
  }

  const currentAmounts = info?.currentAmountsInfo?.currentAmounts || [];

  // Group by alcanceCode
  const groupedAmounts = currentAmounts.reduce((acc, item) => {
    if (!acc[item.alcanceCode]) {
      acc[item.alcanceCode] = [];
    }
    acc[item.alcanceCode].push(item);
    return acc;
  }, {});

  // Calculate totals for each alcanceCode
  const alcanceCodeTotals = {};
  Object.keys(groupedAmounts).forEach((alcanceCode) => {
    alcanceCodeTotals[alcanceCode] = groupedAmounts[alcanceCode].reduce(
      (total, item) => ({
        amountCurrent: total.amountCurrent + item.amountCurrent,
        amountMax: total.amountMax + item.amountMax,
        amountRemaining: total.amountRemaining + item.amountRemaining,
      }),
      { amountCurrent: 0, amountMax: 0, amountRemaining: 0 }
    );
  });

  const toggleGroup = (alcanceCode) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [alcanceCode]: !prev[alcanceCode],
    }));
  };

  return (
    <Dialog
      open={true}
      maxWidth="xl"
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-ems-current-amounts-dialog-title"
      PaperProps={{
        className: classes.dialogPaper,
        elevation: 3,
      }}
    >
      <div>
        <DialogTitle
          className={classes.titleBox}
          id="draggable-ems-current-amounts-dialog-title"
          disableTypography
        >
          <EmployeeStoreLogo />
        </DialogTitle>
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isNil(info)) {
              info.closeCurrentAmountsInfoDialog();
            }
          }}
          className={classes.closeBtn}
          style={{}}
        >
          <Icon className="fas fa-times-circle" />
        </IconButton>
      </div>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.titleText}>
          {t("EMPLOYEE_STORE.AMOUNTS_TABLE_TITLE")}
        </Typography>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table stickyHeader aria-label="current amounts table">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell} align="center">
                  {t("EMPLOYEE_STORE.AMOUNTS_TABLE_COL_SCOPE")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  {t("EMPLOYEE_STORE.AMOUNTS_TABLE_COL_VALUE")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  {t("EMPLOYEE_STORE.AMOUNTS_TABLE_COL_CURRENT_AMOUNT", {
                    currency: MAP_COUNTRY_CURRENCY?.[auth?.emsCountry],
                  })}
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  {t("EMPLOYEE_STORE.AMOUNTS_TABLE_COL_MAX_AMOUNT", {
                    currency: MAP_COUNTRY_CURRENCY?.[auth?.emsCountry],
                  })}
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  {t("EMPLOYEE_STORE.AMOUNTS_TABLE_COL_REMAINING_AMOUNT", {
                    currency: MAP_COUNTRY_CURRENCY?.[auth?.emsCountry],
                  })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(groupedAmounts).map((alcanceCode) => {
                const hasChildren = groupedAmounts[alcanceCode].length > 1;
                const isExpanded = expandedGroups[alcanceCode] || !hasChildren;

                return (
                  <React.Fragment key={alcanceCode}>
                    {/* Parent row */}
                    <TableRow
                      onClick={() => hasChildren && toggleGroup(alcanceCode)}
                      className={
                        hasChildren ? classes.parentRow : classes.tableRow
                      }
                    >
                      <TableCell
                        align="center"
                        className={classes.expanderCell}
                      >
                        {hasChildren && (
                          <Icon
                            className={
                              !isExpanded
                                ? "fas fa-chevron-down"
                                : "fas fa-chevron-up"
                            }
                            style={{ marginRight: 6 }}
                            fontSize="small"
                          />
                        )}
                        {t(MAP_ALCANCE_CODES[alcanceCode])}
                      </TableCell>
                      <TableCell align="center" className={classes.commonCell}>
                        {hasChildren
                          ? ""
                          : groupedAmounts[alcanceCode][0].alcanceValueDesc}
                      </TableCell>
                      <TableCell align="center" className={classes.commonCell}>
                        {hasChildren
                          ? ""
                          : formatPriceWithCountry({
                              price:
                                groupedAmounts[alcanceCode][0].amountCurrent,
                              country: auth?.emsCountry,
                            })}
                      </TableCell>
                      <TableCell align="center" className={classes.commonCell}>
                        {hasChildren
                          ? ""
                          : formatPriceWithCountry({
                              price: groupedAmounts[alcanceCode][0].amountMax,
                              country: auth?.emsCountry,
                            })}
                      </TableCell>
                      <TableCell align="center" className={classes.commonCell}>
                        {hasChildren
                          ? ""
                          : formatPriceWithCountry({
                              price:
                                groupedAmounts[alcanceCode][0].amountRemaining,
                              country: auth?.emsCountry,
                            })}
                      </TableCell>
                    </TableRow>

                    {/* Child rows */}
                    {hasChildren && (
                      <TableRow>
                        <TableCell style={{ padding: 0 }} colSpan={5}>
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Table size="small">
                              <TableBody>
                                {groupedAmounts[alcanceCode].map((row) => (
                                  <TableRow
                                    key={`${row.alcanceCode}-${row.alcanceValueDesc}`}
                                    className={classes.childRow}
                                  >
                                    <TableCell
                                      align="center"
                                      className={classes.indentCell}
                                    />
                                    <TableCell
                                      align="center"
                                      className={classes.commonCell}
                                    >
                                      {`${row.alcanceValueDesc} (${row.alcanceValue})`}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      className={classes.commonCell}
                                    >
                                      {formatPriceWithCountry({
                                        price: row.amountCurrent,
                                        country: auth?.emsCountry,
                                      })}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      className={classes.commonCell}
                                    >
                                      {formatPriceWithCountry({
                                        price: row.amountMax,
                                        country: auth?.emsCountry,
                                      })}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      className={classes.commonCell}
                                    >
                                      {formatPriceWithCountry({
                                        price: row.amountRemaining,
                                        country: auth?.emsCountry,
                                      })}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
